'use client';

import React from 'react';
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { brand } from '@/config/brand';
import { isPlaceholderValue } from '@/lib/contact';
import { useTranslations } from "@/lib/i18n-client";

import { getFaqs } from '@/lib/faq-data';

export default function FAQ() {
  const { t } = useTranslations();
  const hasPhoneContact = !isPlaceholderValue(brand.contact.phone);
  const faqs = getFaqs((key, fallback) => t(key, fallback));
  return (
    <section id="faq" className="py-16 relative">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-hero" />
      <div className="absolute top-20 left-10 w-72 h-72 bg-primary/20 rounded-full blur-3xl animate-float" />
      <div
        className="absolute bottom-20 right-10 w-96 h-96 bg-secondary/20 rounded-full blur-3xl animate-float"
        style={{ animationDelay: "1s" }}
      />

      {/* Additional gradient blur effects */}
      <div
        className="gradient-blur-1 bg-primary/10 animate-float"
        style={{ top: "25%", left: "33%", animationDelay: "2s" }}
      />
      <div
        className="gradient-blur-2 bg-secondary/15 animate-float"
        style={{ top: "75%", right: "25%", animationDelay: "3s" }}
      />
      <div
        className="gradient-blur-3 bg-warning/10 animate-float"
        style={{ top: "50%", left: "50%", animationDelay: "1.5s" }}
      />
      {/* Gradient blur effects */}
      <div
        className="gradient-blur-2 bg-primary/6 animate-float"
        style={{ top: "25%", left: "33%", animationDelay: "1.7s" }}
      />
      <div
        className="gradient-blur-3 bg-secondary/8 animate-float"
        style={{ top: "75%", right: "33%", animationDelay: "3.2s" }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            {t("faq.title", "Frequently Asked")} {" "}
            <span className="bg-gradient-primary bg-clip-text text-transparent">
              {t("faq.titleHighlight", "Questions")}
            </span>
          </h2>
          <p className="text-xl text-muted-foreground">
            {t("faq.subtitle", "Get answers to common questions about {brand}")
              .replace("{brand}", brand.brand.displayName)}
          </p>
        </div>

        <div className="glass rounded-2xl p-8 ">
          <Accordion type="single" collapsible className="space-y-4 ">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={faq.id}
                value={faq.id}
                className="border border-border/50 rounded-lg px-6 animate-fade-in card-pattern-static"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <AccordionTrigger className="text-left hover:text-primary transition-smooth py-6">
                  <span className="font-semibold">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div className="text-center mt-12">
          <p className="text-muted-foreground mb-4">
            {t("faq.footer", "Still have questions? We're here to help.")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`mailto:${brand.contact.email}`}
              className="glass rounded-lg px-6 py-3 hover:bg-white/10 transition-smooth"
            >
              📧 {brand.contact.email}
            </a>
            {hasPhoneContact ? (
              <a
                href={`tel:${brand.contact.phone}`}
                className="glass rounded-lg px-6 py-3 hover:bg-white/10 transition-smooth"
              >
                📞 {brand.contact.phone}
              </a>
            ) : (
              <span className="glass rounded-lg px-6 py-3 text-muted-foreground">
                💬 {t("faq.chatSupport", "24/7 chat support")}
              </span>
            )}
          </div>
        </div>

        {/* Troubleshooting guides — internal links boost authority on strike-zone posts */}
        <div className="mt-10 pt-8 border-t border-border/40">
          <p className="text-center text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
            {t("faq.troubleshootingTitle", "Common iPhone troubleshooting")}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              href="/blog/fix-no-service-on-esim-iphone-15-proven-steps/"
              className="glass rounded-lg px-4 py-2 text-sm hover:bg-white/10 transition-smooth border border-border/40"
            >
              {t("faq.troubleshootingNoService", "iPhone 15 eSIM: No Service fixes")}
            </Link>
            <Link
              href="/blog/fix-esim-cannot-connect-to-network-iphone-15-proven-solutions/"
              className="glass rounded-lg px-4 py-2 text-sm hover:bg-white/10 transition-smooth border border-border/40"
            >
              {t("faq.troubleshootingNetwork", "iPhone 15 eSIM: Cannot connect to network")}
            </Link>
            <Link
              href="/installation-guide/"
              className="glass rounded-lg px-4 py-2 text-sm hover:bg-white/10 transition-smooth border border-border/40"
            >
              {t("faq.troubleshootingInstall", "Full installation guide")}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
